import { useState, useEffect } from "react";
import { useTheme } from "../../../contexts/ThemeContext";

const LOGO = "https://public.readdy.ai/ai/img_res/c055e417-8161-4888-b526-7bdc05bd652e.png";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id: string) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const navLinks = [
    { label: "How It Works", id: "how-it-works" },
    { label: "Benefits", id: "benefits" },
    { label: "Industries", id: "industries" },
    { label: "Process", id: "deployment" },
  ];

  const navBg = scrolled
    ? isDark
      ? "bg-black/85 backdrop-blur-2xl border-b border-white/[0.07]"
      : "bg-white/90 backdrop-blur-2xl border-b border-black/[0.08]"
    : "bg-transparent";

  const linkColor = isDark ? "text-white/60 hover:text-white" : "text-black/60 hover:text-black";
  const mobileMenuBg = isDark ? "bg-black/97 border-white/[0.06]" : "bg-white/97 border-black/[0.08]";
  const mobileLinkColor = isDark ? "text-white/70" : "text-black/70";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`} style={{ height: "80px" }}>
      <div className="h-full mx-auto px-6 md:px-12 flex items-center justify-between" style={{ maxWidth: "1280px" }}>

        {/* Logo */}
        <a href="/" className="flex items-center cursor-pointer shrink-0">
          <img src={LOGO} alt="AI Talent Launch" className="object-contain" style={{ width: "90px", height: "90px" }} />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className={`${linkColor} text-[13px] font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap`}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* Desktop Right */}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`relative w-[52px] h-[28px] rounded-full transition-all duration-300 cursor-pointer shrink-0 ${
              isDark ? "bg-white/10 border border-white/15" : "bg-black/8 border border-black/12"
            }`}
          >
            <span
              className={`absolute top-[3px] left-[3px] w-[20px] h-[20px] rounded-full flex items-center justify-center transition-all duration-300 ${
                isDark ? "translate-x-[24px] bg-white" : "translate-x-0 bg-[#1d1d1f]"
              }`}
            >
              <i className={`text-[10px] ${isDark ? "ri-moon-line text-black" : "ri-sun-line text-white"}`}></i>
            </span>
          </button>

          <button
            onClick={() => scrollTo("contact")}
            className={`text-[13px] font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${linkColor}`}
          >
            Contact
          </button>
          <button
            onClick={() => scrollTo("contact")}
            className="bg-[#F97316] text-white text-[13px] font-semibold px-5 py-2 rounded-full hover:bg-[#FB923C] hover:scale-[1.02] transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Get Started
          </button>
        </div>

        {/* Mobile buttons */}
        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className={`w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
              isDark ? "text-white/70 hover:text-white" : "text-black/60 hover:text-black"
            }`}
          >
            <i className={isDark ? "ri-sun-line text-lg" : "ri-moon-line text-lg"}></i>
          </button>
          <button
            className={`w-10 h-10 flex items-center justify-center cursor-pointer ${isDark ? "text-white" : "text-black"}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`ri-${menuOpen ? "close" : "menu-3"}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className={`md:hidden backdrop-blur-2xl border-t px-6 py-6 flex flex-col gap-5 ${mobileMenuBg}`}>
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className={`${mobileLinkColor} text-base font-medium text-left cursor-pointer whitespace-nowrap`}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => scrollTo("contact")}
            className="bg-[#F97316] text-white font-semibold px-6 py-3 rounded-full mt-2 cursor-pointer whitespace-nowrap hover:bg-[#FB923C] transition-colors"
          >
            Get Started
          </button>
        </div>
      )}
    </nav>
  );
}
